/**
 * CallPopup — globales 3CX-Anruf-Popup (feature/crm2-3cx-integration).
 *
 * Pollt GET /v2/admin/3cx/active-call alle 4s — aber NUR wenn eingeloggt und
 * eine eigene 3CX-Extension in Profil-Einstellungen gesetzt ist
 * (usePermissions().threeCxExtension, gemeinsam mit /admin/me geladen).
 * Ohne Extension bleibt der Poll-Timer aus (kein unnötiger Request-Traffic).
 *
 * Zeigt bei status "ringing"/"connected" eine dezente Karte unten rechts:
 * Kontaktname/-nummer, PB/BK-Badge, Link zum Profil, Dismiss-Button.
 * "Dismiss" merkt sich die call_id, damit dieselbe Karte nicht sofort wieder
 * erscheint (ein neuer Call — andere call_id — zeigt wieder an).
 *
 * Gemountet einmal global in App.tsx (analog ToastHost).
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { v2ThreeCx, type ThreeCxCall } from '../../lib/threeCx';
import { getThemeColors, BRAND, BRAND_LIGHT, BRAND_DARK } from '../../lib/theme';
import { Icon } from '../icons';
import { parsePath, navigateGlobal } from '../../lib/route';

const POLL_INTERVAL_MS = 4000;

interface CallPopupProps {
  dark: boolean;
}

export function CallPopup({ dark }: CallPopupProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { threeCxExtension } = usePermissions();

  const [call, setCall] = useState<ThreeCxCall | null>(null);
  const [dismissedCallId, setDismissedCallId] = useState<string | null>(null);

  const shouldPoll = isAuthenticated && !!threeCxExtension;

  useEffect(() => {
    if (!shouldPoll) {
      setCall(null);
      return;
    }
    let alive = true;

    const poll = async () => {
      try {
        const active = await v2ThreeCx.activeCall();
        if (alive) setCall(active);
      } catch {
        // Silent — a failed poll just skips this tick, no toast spam every 4s.
      }
    };

    void poll();
    const interval = window.setInterval(poll, POLL_INTERVAL_MS);
    return () => { alive = false; window.clearInterval(interval); };
  }, [shouldPoll]);

  if (!call || (call.status !== 'ringing' && call.status !== 'connected')) return null;
  if (dismissedCallId === call.call_id) return null;

  const handleOpenProfile = () => {
    if (!call.profile_url) return;
    try {
      const r = parsePath(new URL(call.profile_url).pathname);
      navigateGlobal(r.page, r.sub, r.tab, r.extra);
    } catch {
      /* malformed URL — link simply doesn't navigate */
    }
  };

  const displayName = call.contact_name ?? call.external_number ?? tr('call_popup.unknown_caller');
  const isRinging = call.status === 'ringing';

  return (
    <div
      role="status"
      aria-live="polite"
      className="fade-in"
      style={{
        position: 'fixed', right: 20, bottom: 20, zIndex: 1000,
        width: 300, borderRadius: 14,
        background: t.card, border: `1px solid ${t.border}`,
        boxShadow: '0 12px 32px -8px rgba(16,24,40,0.25)',
        overflow: 'hidden',
      }}
    >
      <div style={{
        padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8,
        background: isRinging ? (dark ? '#3a2a0f' : '#fffaeb') : (dark ? '#0f3a23' : '#ecfdf3'),
        borderBottom: `1px solid ${t.borderSoft}`,
      }}>
        <span style={{
          width: 8, height: 8, borderRadius: '50%',
          background: isRinging ? '#f79009' : '#12b76a',
          animation: isRinging ? 'sh24-pulse 1.2s ease-in-out infinite' : undefined,
          flexShrink: 0,
        }} />
        <span style={{
          fontSize: 11.5, fontWeight: 600, letterSpacing: '0.02em',
          color: isRinging ? (dark ? '#fcd34d' : '#b54708') : (dark ? '#86efac' : '#027a48'),
          flex: 1,
        }}>
          {isRinging
            ? (call.direction === 'inbound' ? tr('call_popup.status_ringing_in') : tr('call_popup.status_ringing_out'))
            : tr('call_popup.status_connected')}
        </span>
        <button
          onClick={() => setDismissedCallId(call.call_id)}
          aria-label={tr('common.action.close')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: 2 }}
        >
          <Icon.Close c={t.muted} size={14} />
        </button>
      </div>

      <div style={{ padding: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: call.profile_url ? 12 : 0 }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
            background: BRAND_LIGHT, color: BRAND,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 15, fontWeight: 700,
          }}>
            <Icon.Phone c={BRAND} />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{
              fontSize: 14, fontWeight: 600, color: t.heading,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {displayName}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
              {call.external_number && call.contact_name && (
                <span style={{ fontSize: 11.5, color: t.muted }}>{call.external_number}</span>
              )}
              {call.contact_type && (
                <span style={{
                  fontSize: 10.5, fontWeight: 600, borderRadius: 999, padding: '1px 8px',
                  background: dark ? '#2a1222' : BRAND_LIGHT,
                  color: dark ? '#f49dd1' : BRAND_DARK,
                }}>
                  {call.contact_type === 'pb' ? tr('call_popup.badge_pb') : tr('call_popup.badge_bk')}
                </span>
              )}
            </div>
          </div>
        </div>

        {call.profile_url && (
          <button
            onClick={handleOpenProfile}
            style={{
              width: '100%', fontSize: 12.5, fontWeight: 600,
              background: BRAND_LIGHT, color: BRAND, border: `1px solid ${BRAND}`,
              borderRadius: 8, padding: '7px 0', cursor: 'pointer',
            }}
          >
            {tr('call_popup.open_profile')}
          </button>
        )}
      </div>
    </div>
  );
}
